import { getCsrfToken } from "@/app/_utils/csrf";

export type Show = {
  id: string;
  location_id: string;
  location_name: string;
  title: string;
  start: string;
  end: string;
  sold: number;
  ticket_price: number;
  ticket_url: string;
  capacity: number;
  notes: string;
  img_url: string;
  description: string;
  variant_id: string;
  variant_name: string;
  background_color: string;
  config: any;
  stripe_product_id?: string | null;
  published?: boolean;
  slug?: string;
  slug_history?: { slug: string; created_at: string }[];
};

const csrf = () => getCsrfToken();

const jsonInit = (
  method: string,
  body?: object
): RequestInit => ({
  method,
  credentials: "include",
  headers: {
    "Content-Type": "application/json",
    "X-CSRFToken": csrf(),
  },
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});

class ShowService {
  static instance: ShowService;
  shows: Show[] = [];
  async getShow({
    id,
    past = false,
    future = false,
    year,
    month,
    q,
  }: {
    id?: any;
    past?: boolean;
    future?: boolean;
    year?: number;
    month?: number;
    q?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (id) queryParams.append("id", id);
    if (past) queryParams.append("past", true.toString());
    if (future) queryParams.append("future", true.toString());
    if (year != null) queryParams.append("year", year.toString());
    if (month != null) queryParams.append("month", month.toString());
    if (q) queryParams.append("q", q);

    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/?${queryParams}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return await res.json();
  }

  async getLocations() {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/locations/`,
      {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return await res.json();
  }

  async getRelatedShowDetails(id: string) {
    const queryParams = new URLSearchParams();
    if (id) queryParams.append("id", id);

    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/relatedShows/?${queryParams}`,
      {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return await res.json();
  }

  async getGuestList({
    show_id,
    intake = false,
    masterlist = false,
  }: {
    show_id: string;
    intake?: boolean;
    masterlist?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    if (show_id) queryParams.append("show_id", show_id);
    if (masterlist) queryParams.append("masterlist", masterlist.toString());
    if (intake) queryParams.append("intake", intake.toString());
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/order/?${queryParams}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const guests = await res.json();
    let count = 0;
    let total = 0;
    for (let i = 0; i < guests.length; i++) {
      count += Number(guests[i].redeemed);
      total += Number(guests[i].quantity);
    }
    return { guests, count, total };
  }

  async addGuest(order: any) {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/order/`,
      jsonInit("POST", order)
    );
    return res;
  }

  async exportGuestList(show_id: any, masterlist = false) {
    const queryParams = new URLSearchParams();
    if (show_id) queryParams.append("show_id", show_id);
    if (masterlist) queryParams.append("masterlist", masterlist.toString());
    queryParams.append("export", true.toString());
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/order/?${queryParams}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return await res.blob();
  }

  async updateShow(show: any) {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/`,
      jsonInit("PUT", show)
    );
    return res.json();
  }

  async getLiveSales(showId: string) {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/live/?show_id=${showId}`,
      { method: "GET", credentials: "include" }
    );
    if (!res.ok) throw new Error(`live sales ${res.status}`);
    return res.json();
  }

  async getCheckouts(showId: string, onlyOpen: boolean) {
    const status = onlyOpen ? "open" : "all";
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/checkouts/?show_id=${showId}&status=${status}`,
      { method: "GET", credentials: "include" }
    );
    if (!res.ok) throw new Error(`checkouts ${res.status}`);
    return res.json();
  }

  async expireCheckouts(showId: string, sessionIds: string[] | "all_open") {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/checkouts/expire/`,
      jsonInit("POST", { show_id: showId, session_ids: sessionIds })
    );
    if (!res.ok) throw new Error(`expire checkouts ${res.status}`);
    return res.json();
  }

  async confirmCheckout(showId: string, sessionId: string): Promise<{ outcome: "paid" | "unpaid" | "aborted" | "failed" }> {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/checkouts/confirm/`,
      jsonInit("POST", { show_id: showId, session_id: sessionId })
    );
    if (!res.ok) throw new Error(`confirm checkout ${res.status}`);
    return res.json();
  }

  async publishShow(id: string, published: boolean) {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/publish/`,
      jsonInit("POST", { id, published })
    );
    return res.json();
  }

  async regenerateSlug(id: string) {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/regenerate-slug/`,
      jsonInit("POST", { showId: id })
    );
    return res.json();
  }

  async setSlug(id: string, slug: string) {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/set-slug/`,
      jsonInit("POST", { showId: id, slug })
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        typeof json?.error === "string" ? json.error : "Failed to update slug"
      );
    }
    return json as { id: string; slug: string };
  }

  async downloadIntakeTemplate() {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/intake/template/`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    return await res.blob();
  }

  async addIntakeGuests(showId: any, isCSV: any, guestData: any) {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}show/intake/`,
      jsonInit("POST", {
        isCSV,
        showId,
        data: guestData,
      })
    );
    return res;
  }

  async delete() {}
}

export default new ShowService();
